import React, { useMemo } from "react";
import CloseIcon from "../components/icons/CloseIcon";
import ProductArtworkPreview from "./ProductArtworkPreview";
import { useModalMotion } from "../hooks/useModalMotion";
import { cartFeeBreakdown } from "./checkoutHelpers";
import { cartArtUrl, cartLineKey, formatPrice } from "./shopUtils";

function EmptyCartIcon() {
  return (
    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" aria-hidden="true">
      <path
        d="M31.1 26C32.6 26 33.92 25.18 34.6 23.94L41.76 10.96C42.5 9.64 41.54 8 40.02 8H10.42L8.54 4H2V8H6L13.2 23.18L10.5 28.06C9.04 30.74 10.96 34 14 34H38V30H14L16.2 26H31.1ZM12.32 12H36.62L31.1 22H17.06L12.32 12ZM14 36C11.8 36 10.02 37.8 10.02 40C10.02 42.2 11.8 44 14 44C16.2 44 18 42.2 18 40C18 37.8 16.2 36 14 36ZM34 36C31.8 36 30.02 37.8 30.02 40C30.02 42.2 31.8 44 34 44C36.2 44 38 42.2 38 40C38 37.8 36.2 36 34 36Z"
        fill="currentColor"
      />
    </svg>
  );
}

function PayArrow() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ transform: "rotate(180deg)" }}
    >
      <path d="m12 19-7-7 7-7" />
      <path d="M19 12H5" />
    </svg>
  );
}

function itemName(item) {
  if (typeof item?.name === "string") return item.name;
  if (item?.name && typeof item.name === "object") return item.name.name || "Product";
  return "Product";
}

function itemVariant(item) {
  if (item?.isPack || String(item?.type || "").toLowerCase() === "pack") {
    const count = Array.isArray(item.products) ? item.products.length : 0;
    return count ? `Pack · ${count} items` : "Pack";
  }
  return [item?.size, item?.color].filter(Boolean).join(" · ");
}

function linePrice(item) {
  const unit =
    typeof item.price === "number" ? item.price : parseFloat(String(item.price || "0")) || 0;
  return unit * (item.quantity || 0);
}

function CartModal({
  items = [],
  onClose,
  onRemove,
  onPay,
  onPayOnReception,
  onReviewCart,
  paying = false,
  museumFees = null,
}) {
  const { requestClose, motionClassName } = useModalMotion(onClose, {
    duration: 320,
  });
  const pricing = useMemo(
    () => cartFeeBreakdown(items, museumFees),
    [items, museumFees]
  );
  const hasItems = items.length > 0;
  const itemCount = useMemo(
    () => items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0),
    [items]
  );

  return (
    <div
      className={`cartModal${motionClassName}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="cartModalTitle"
    >
      <button
        type="button"
        className="cartModal__backdrop"
        onClick={() => {
          if (paying) return;
          requestClose();
        }}
        aria-label="Close cart"
      />
      <div className="cartModal__card" onClick={(event) => event.stopPropagation()}>
        <header className="cartModal__header">
          <div className="cartModal__heading">
            <h2 id="cartModalTitle" className="cartModal__title">
              Your Cart
            </h2>
            {hasItems ? (
              <span className="cartModal__count">
                {itemCount} {itemCount === 1 ? "item" : "items"}
              </span>
            ) : null}
          </div>
          <button
            type="button"
            className="cartModal__close"
            onClick={requestClose}
            disabled={paying}
            aria-label="Close"
          >
            <CloseIcon />
          </button>
        </header>

        {hasItems ? (
          <div className="cartModal__list" role="list">
            {items.map((item) => (
              <article
                className="cartModal__item"
                role="listitem"
                key={cartLineKey(item)}
              >
                <div className="cartModal__thumb">
                  <ProductArtworkPreview
                    productImage={item.imageUrl || item.imgURL}
                    artworkImage={
                      item.merchGeneratedMockup ? "" : cartArtUrl(item)
                    }
                    productName={itemName(item)}
                    skuCode={item.sku_code}
                    placement={item.placement}
                  />
                </div>
                <div className="cartModal__itemBody">
                  <div className="cartModal__itemTop">
                    <div className="cartModal__meta">
                      <h3 className="cartModal__name">{itemName(item)}</h3>
                      {itemVariant(item) ? (
                        <p className="cartModal__variant">{itemVariant(item)}</p>
                      ) : null}
                    </div>
                    <p className="cartModal__linePrice">
                      {formatPrice(linePrice(item))}
                    </p>
                  </div>
                  <div className="cartModal__itemBottom">
                    <span className="cartModal__qtyValue">
                      Qty {item.quantity || 0}
                    </span>
                    <button
                      type="button"
                      className="cartModal__remove"
                      onClick={() => onRemove?.(item)}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="cartModal__empty">
            <div className="cartModal__emptyIcon">
              <EmptyCartIcon />
            </div>
            <h3 className="cartModal__emptyTitle">Your cart is empty</h3>
            <p className="cartModal__emptyText">
              Nothing here yet. Add a product from the shop and it will show up in your cart.
            </p>
            <button
              type="button"
              className="cartModal__emptyBtn"
              onClick={requestClose}
            >
              Continue shopping
            </button>
          </div>
        )}

        {hasItems ? (
          <div className="cartModal__footer">
            <div className="cartModal__breakdown">
              <div className="cartModal__breakdownRow">
                <span>Subtotal</span>
                <span>{formatPrice(pricing.subtotal)}</span>
              </div>
              <div className="cartModal__breakdownRow">
                <span>Processing fee ({pricing.processingFeePercent}%)</span>
                <span>{formatPrice(pricing.processingFee)}</span>
              </div>
              <div className="cartModal__breakdownRow">
                <span>Sales tax ({pricing.salesTaxPercent}%)</span>
                <span>{formatPrice(pricing.salesTax)}</span>
              </div>
              <div className="cartModal__total">
                <span>Total</span>
                <strong>{formatPrice(pricing.total)}</strong>
              </div>
            </div>
            <div className="cartModal__actions">
              {onReviewCart ? (
                <button
                  type="button"
                  className="cartModal__secondary"
                  disabled={paying}
                  onClick={onReviewCart}
                >
                  Review cart
                </button>
              ) : null}
              {onPayOnReception ? (
                <button
                  type="button"
                  className="cartModal__secondary"
                  disabled={paying}
                  onClick={onPayOnReception}
                >
                  Pay at counter
                </button>
              ) : null}
              <button
                type="button"
                className={`cartModal__pay${paying ? " cartModal__pay--loading" : ""}`}
                disabled={paying}
                aria-busy={paying || undefined}
                onClick={() => {
                  if (paying) return;
                  onPay?.();
                }}
              >
                {paying ? (
                  <>
                    <span className="cartModal__paySpinner" aria-hidden="true" />
                    Loading...
                  </>
                ) : (
                  <>
                    Pay now <PayArrow />
                  </>
                )}
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default CartModal;
